import { useEffect, useState } from "react";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../api/firebase/references";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { HealthCareFacilityModel } from "../../data-model/healthFacility";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../store/UserGeneralInfoStore";

/**
 * Renders the embedded analytics dashboard for the active healthcare facility.
 *
 * Resolves the facility's analytics URL from its Firestore document and shows it
 * inside a full-height iframe, displaying a loader while the document is fetched.
 *
 * @returns The analytics page layout containing the embedded analytics iframe.
 */
const AnalyticsNew = () => {
  const [healthFacilityDataState, setHealthFacilityDataState] = useState<{
    data?: HealthCareFacilityModel;
    loading: boolean;
    error: string | null;
  }>({
    data: undefined,
    loading: false,
    error: null,
  });

  const internalOrgId = UserStore.getItem(
    UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
  );
  const healthcareFacilityId = UserStore.getItem(
    UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID,
  );

  /**
   * Fetches the active facility's Firestore document and stores it in state.
   *
   * Prefers the internal organization facility ID and falls back to the
   * healthcare facility ID so the correct analytics URL is loaded for both
   * internal and external organizations.
   *
   * @returns A promise that resolves once the facility data state is updated.
   */
  const fetchHealthFacilityData = async () => {
    const computedOrgId = internalOrgId || healthcareFacilityId;
    setHealthFacilityDataState({ data: undefined, loading: true, error: null });
    const healthFacilityData =
      await FirestoreService.getDocument<HealthCareFacilityModel>(
        FirestoreDocumentReference.healthFacility(computedOrgId),
      );
    setHealthFacilityDataState({
      data: healthFacilityData,
      loading: false,
      error: null,
    });
  };
  useEffect(() => {
    fetchHealthFacilityData();
  }, [healthcareFacilityId]);
  return (
    <div className="new-analytics-page--layout flex h-full items-center justify-center rounded-lg bg-primaryGray-16 p-4">
      {healthFacilityDataState?.loading ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoaderMessage message="Loading analytics..." />
        </div>
      ) : (
        <>
          <iframe
            title="Analytics"
            width="100%"
            height="100%"
            src={healthFacilityDataState?.data?.analytics_url ?? ""}
            allowFullScreen={true}
          ></iframe>
        </>
      )}
    </div>
  );
};

export default AnalyticsNew;
